/**
 * Portfolio Dashboard Page
 * Aggregated "All Engagements" analytics dashboard
 */

import { useState, useMemo, useCallback } from 'react'
import { usePageTitle } from '@/hooks/use-page-title'
import { useDashboardData } from '@/hooks/use-dashboard-data'
import { useBranches } from '@/hooks/use-branches'
import { useEngagementLookups } from '@/hooks/use-engagement-lookups'
import { useOrganizations } from '@/hooks/use-organizations'
import type { Organization } from '@/hooks/use-organizations'
import { ENGAGEMENT_STATUS } from '@/lib/constants/engagement-codes'
import { useEngagementDashboardApi } from '@/hooks/use-engagement-dashboard-api'
import type { EngagementDashboardFilters } from '@/hooks/use-engagement-dashboard-api'
import type { LookupValue } from '@/types/api/engagement'
import { PortfolioInfoBar } from '@/components/dashboard/PortfolioInfoBar'
import { PortfolioKPICards } from '@/components/dashboard/PortfolioKPICards'
import type { EngagementStatusCounts } from '@/components/dashboard/PortfolioKPICards'
import { ProfitabilityChart } from '@/components/dashboard/ProfitabilityChart'
import { BurndownChart } from '@/components/dashboard/BurndownChart'
import { ContractorTypeChart } from '@/components/dashboard/ContractorTypeChart'
import { GrossMarginByBranchChart } from '@/components/dashboard/GrossMarginByBranchChart'
import type { BranchMarginData } from '@/components/dashboard/GrossMarginByBranchChart'
import { GrossProfitChart } from '@/components/dashboard/GrossProfitChart'
import { RevenueRecognizedChart } from '@/components/dashboard/RevenueRecognizedChart'
import { PortfolioTeamHoursTable } from '@/components/dashboard/PortfolioTeamHoursTable'
import { PortfolioEngagementTable } from '@/components/dashboard/PortfolioEngagementTable'
import { UtilizationChart } from '@/components/dashboard/UtilizationChart'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { VirtualizedSelect } from '@/components/ui/virtualized-select'
import { Label } from '@/components/ui/label'

const MONTHS = [
  { value: '01', label: 'Jan' }, { value: '02', label: 'Feb' }, { value: '03', label: 'Mar' },
  { value: '04', label: 'Apr' }, { value: '05', label: 'May' }, { value: '06', label: 'Jun' },
  { value: '07', label: 'Jul' }, { value: '08', label: 'Aug' }, { value: '09', label: 'Sep' },
  { value: '10', label: 'Oct' }, { value: '11', label: 'Nov' }, { value: '12', label: 'Dec' },
]

interface FilterState {
  branch: string
  client: string     // org.Organization Id as string, or 'all'
  status: string
  billingType: string
  engagementLead: string
  workerType: string
  engagementType: string
  fromMonth: string   // "YYYY-MM"
  toMonth: string     // "YYYY-MM"
}

function MonthYearPicker({
  label,
  value,
  onChange,
  yearRange,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  yearRange: number[]
}) {
  const [year, month] = value.split('-')
  return (
    <div className="flex items-center gap-2">
      <Label className="text-xs text-muted-foreground whitespace-nowrap">{label}</Label>
      <Select value={month} onValueChange={m => onChange(`${year}-${m}`)}>
        <SelectTrigger className="h-8 w-[70px] text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {MONTHS.map(m => (
            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={year} onValueChange={y => onChange(`${y}-${month}`)}>
        <SelectTrigger className="h-8 w-[80px] text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {yearRange.map(y => (
            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function FilterBar({
  filters,
  onChange,
  branches,
  clientOrganizations,
  leadOptions,
  yearRange,
  billingTypes,
  engagementTypes,
  engagementStatuses,
  workerTypeOptions,
}: {
  filters: FilterState
  onChange: (key: keyof FilterState, value: string) => void
  branches: { Code: string; Name: string }[]
  clientOrganizations: Organization[]
  leadOptions: string[]
  yearRange: number[]
  billingTypes: LookupValue[]
  engagementTypes: LookupValue[]
  engagementStatuses: LookupValue[]
  workerTypeOptions: string[]
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 bg-background border rounded-md flex-wrap">
      <div className="flex items-center gap-2">
        <Label className="text-xs text-muted-foreground whitespace-nowrap">Branch</Label>
        <Select value={filters.branch} onValueChange={v => onChange('branch', v)}>
          <SelectTrigger className="h-8 w-[140px] text-xs">
            <SelectValue placeholder="All Branches" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Branches</SelectItem>
            {branches.map(b => (
              <SelectItem key={b.Code} value={b.Code}>{b.Name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="h-4 w-px bg-border" />

      <div className="flex items-center gap-2">
        <Label className="text-xs text-muted-foreground whitespace-nowrap">Client</Label>
        <div className="w-[180px]">
          <VirtualizedSelect
            options={clientOrganizations}
            value={filters.client === 'all' ? '' : filters.client}
            onValueChange={v => onChange('client', v === '' ? 'all' : v)}
            getItemLabel={org => org.Code ? `${org.DisplayName} (${org.Code})` : org.DisplayName}
            getItemValue={org => String(org.Id)}
            placeholder="All Clients"
            searchPlaceholder="Search clients..."
            className="h-8 w-full text-xs font-normal"
            allowClear
          />
        </div>
      </div>

      <div className="h-4 w-px bg-border" />

      <div className="flex items-center gap-2">
        <Label className="text-xs text-muted-foreground whitespace-nowrap">Eng. Lead</Label>
        <Select value={filters.engagementLead} onValueChange={v => onChange('engagementLead', v)}>
          <SelectTrigger className="h-8 w-[150px] text-xs">
            <SelectValue placeholder="All Leads" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Leads</SelectItem>
            {leadOptions.map(l => (
              <SelectItem key={l} value={l}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="h-4 w-px bg-border" />

      <div className="flex items-center gap-2">
        <Label className="text-xs text-muted-foreground whitespace-nowrap">Status</Label>
        <Select value={filters.status} onValueChange={v => onChange('status', v)}>
          <SelectTrigger className="h-8 w-[150px] text-xs">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {engagementStatuses.map(s => (
              <SelectItem key={s.code!} value={s.code!}>{s.value}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="h-4 w-px bg-border" />

      <div className="flex items-center gap-2">
        <Label className="text-xs text-muted-foreground whitespace-nowrap">Billing</Label>
        <Select value={filters.billingType} onValueChange={v => onChange('billingType', v)}>
          <SelectTrigger className="h-8 w-[110px] text-xs">
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {billingTypes.map(bt => (
              <SelectItem key={bt.code!} value={bt.code!}>{bt.value}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="h-4 w-px bg-border" />

      <div className="flex items-center gap-2">
        <Label className="text-xs text-muted-foreground whitespace-nowrap">Worker Type</Label>
        <Select value={filters.workerType} onValueChange={v => onChange('workerType', v)}>
          <SelectTrigger className="h-8 w-[120px] text-xs">
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {workerTypeOptions.map(wt => (
              <SelectItem key={wt} value={wt}>{wt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="h-4 w-px bg-border" />

      <div className="flex items-center gap-2">
        <Label className="text-xs text-muted-foreground whitespace-nowrap">Eng. Type</Label>
        <Select value={filters.engagementType} onValueChange={v => onChange('engagementType', v)}>
          <SelectTrigger className="h-8 w-[130px] text-xs">
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {engagementTypes.map(et => (
              <SelectItem key={et.code!} value={et.code!}>{et.value}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="h-4 w-px bg-border" />

      <MonthYearPicker
        label="From"
        value={filters.fromMonth}
        onChange={v => onChange('fromMonth', v)}
        yearRange={yearRange}
      />

      <MonthYearPicker
        label="To"
        value={filters.toMonth}
        onChange={v => onChange('toMonth', v)}
        yearRange={yearRange}
      />

      {filters.fromMonth > filters.toMonth && (
        <span className="text-xs text-red-600 whitespace-nowrap">"From" must be before "To"</span>
      )}
    </div>
  )
}

export default function PortfolioDashboardPage() {
  usePageTitle('All Engagements — Portfolio Dashboard')

  const currentYear = new Date().getFullYear()
  const currentMonth = String(new Date().getMonth() + 1).padStart(2, '0')
  const yearRange = useMemo(() =>
    Array.from({ length: 10 }, (_, i) => currentYear - 7 + i),
    [currentYear]
  )

  const [filters, setFilters] = useState<FilterState>({
    branch: 'all',
    client: 'all',
    status: 'all',
    billingType: 'all',
    engagementLead: 'all',
    workerType: 'all',
    engagementType: 'all',
    fromMonth: `${currentYear - 1}-${currentMonth}`,
    toMonth: `${currentYear}-${currentMonth}`,
  })

  // Deliberately not part of FilterState: it filters nothing, and only the Engagement Summary table
  // reads it. Keeping it separate also stops a toggle invalidating portfolioFilters and refetching
  // every KPI and chart on the page.
  const [rollUp, setRollUp] = useState(false)

  const { branches } = useBranches()
  const { clientOrganizations } = useOrganizations(['client'])
  const { getByCategory } = useEngagementLookups()

  const billingTypes = useMemo(() => getByCategory('engagement.CodeEngagementBillingType'), [getByCategory])
  const engagementTypes = useMemo(() => getByCategory('engagement.CodeEngagementType'), [getByCategory])
  const engagementStatuses = useMemo(
    () => getByCategory('engagement.CodeEngagementStatus').filter(s => s.code !== ENGAGEMENT_STATUS.REPLACED && s.code !== ENGAGEMENT_STATUS.DELETED),
    [getByCategory]
  )

  const isDateRangeValid = filters.fromMonth <= filters.toMonth

  // Build API filter params — filter state stores SP codes directly from lookups.
  // Returns undefined when the date range is invalid so the hook skips all fetches.
  const portfolioFilters = useMemo<EngagementDashboardFilters | undefined>(() => {
    if (!isDateRangeValid) return undefined
    const f: EngagementDashboardFilters = {
      effectiveFrom: `${filters.fromMonth}-01`,
      effectiveTo: (() => {
        const [y, m] = filters.toMonth.split('-').map(Number)
        const d = new Date(y, m, 0) // last day of month (local)
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      })(),
    }
    if (filters.branch !== 'all') f.branchCode = filters.branch
    if (filters.client !== 'all') f.clientId = Number(filters.client)
    if (filters.status !== 'all') f.status = filters.status
    if (filters.billingType !== 'all') f.billingType = filters.billingType
    if (filters.engagementLead !== 'all') f.engagementLead = filters.engagementLead
    if (filters.workerType !== 'all') f.workerType = filters.workerType
    if (filters.engagementType !== 'all') f.engagementType = filters.engagementType
    return f
  }, [filters, isDateRangeValid])

  // Portfolio-level data via Phoenix API
  const {
    monthlyData,
    isLoading,
    error,
    workerTypeData,
    portfolioTeamHoursData,
    portfolioTeamHoursLoading,
    portfolioTeamHoursError,
    portfolioFinanceData,
    portfolioFinanceError,
    kpiCardRows,
    portfolioChartTotals,
    engagementTableData,
    engagementTableLoading,
    engagementTableError,
    workerTypeOptions,
    utilizationData,
    utilizationFilters,
    utilizationLoading,
  } = useDashboardData({ portfolioMode: true, portfolioFilters, rollUp })

  // The engagement table's exports need the un-rolled rows, which the page never holds while
  // roll-up is on. Fetched on demand at export time rather than kept alongside, so the second set
  // is only paid for when someone actually exports.
  const { getEngagementTable } = useEngagementDashboardApi()
  const fetchUnrolledRows = useCallback(
    () =>
      portfolioFilters
        ? getEngagementTable({ ...portfolioFilters, rollUp: false })
        : Promise.resolve([]),
    [getEngagementTable, portfolioFilters]
  )

  // Chart-local state for utilization controls
  const [utilizationProcurementSource, setUtilizationProcurementSource] = useState<string | null>(null)

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }


  // Derive procurement source options from utilization filter combos
  const procurementSourceOptions = useMemo(
    () => Array.from(new Set(
      utilizationFilters.map(f => f.procurementSourceName).filter(Boolean)
    )).sort() as string[],
    [utilizationFilters]
  )

  // Derive lead options from already-loaded team hours data — avoids a duplicate API call
  const leadOptions = useMemo(
    () => Array.from(new Set(
      (portfolioTeamHoursData?.leads ?? []).map(l => l.leadName).filter(Boolean)
    )).sort() as string[],
    [portfolioTeamHoursData]
  )

  // KPI metrics derived from the portfolio finance report totals
  const portfolioMetrics = useMemo(() => {
    const totals = portfolioFinanceData?.totals
    return {
      contractAmount: totals?.contractAmount ?? 0,
      projectedCost: totals?.projectedCost ?? 0,
      billedToDate: totals?.billedToDate ?? 0,
      revenueToDate: totals?.revenueToDate ?? 0,
      costsToDate: totals?.costsToDate ?? 0,
      grossProfit: totals?.grossProfit ?? 0,
      unbilledRevenue: totals?.unbilledRevenue ?? 0,
      grossMarginPercent: totals?.grossMarginPercent ?? 0,
      billedPercent: (totals?.contractAmount ?? 0) > 0
        ? ((totals?.billedToDate ?? 0) / (totals?.contractAmount ?? 1)) * 100
        : 0,
      remainingContract: totals?.remainingContractAmount ?? 0,
      avgMargin: totals?.grossMarginPercent ?? 0,
      revenueTrend: null,
    }
  }, [portfolioFinanceData])

  // Chart totals from monthly SP data: deduplicated by engagement ID to avoid double-counting per-month rows
  const chartTotals = portfolioChartTotals

  const statusCounts = useMemo<EngagementStatusCounts>(() => ({
    active: kpiCardRows.reduce((s, r) => s + (r.activeEngagements ?? 0), 0),
    pending: kpiCardRows
      .filter(r => r.engagementStatus === 'PendingChange' || r.engagementStatus === 'PendingReview')
      .reduce((s, r) => s + (r.totalEngagements ?? 0), 0),
    complete: kpiCardRows
      .filter(r => r.engagementStatus === 'Completed' || r.engagementStatus === 'Complete')
      .reduce((s, r) => s + (r.totalEngagements ?? 0), 0),
  }), [kpiCardRows])

  // Gross margin grouped by branch from per-engagement finance rows
  const branchMarginData = useMemo<BranchMarginData[]>(() => {
    const rows = portfolioFinanceData?.engagements ?? []
    const branchMap = new Map<string, { revenue: number; grossProfit: number }>()

    rows.forEach(e => {
      const branch = e.branch || 'Unknown'
      const existing = branchMap.get(branch) ?? { revenue: 0, grossProfit: 0 }
      branchMap.set(branch, {
        revenue: existing.revenue + e.revenueRecognition,
        grossProfit: existing.grossProfit + e.grossProfit,
      })
    })

    return Array.from(branchMap.entries()).map(([branch, { revenue, grossProfit }]) => ({
      branch,
      grossMarginPercent: revenue > 0 ? (grossProfit / revenue) * 100 : 0,
    }))
  }, [portfolioFinanceData])

  const branchLabel = filters.branch !== 'all'
    ? branches.find(b => b.Code === filters.branch)?.Name ?? filters.branch
    : 'All Branches'

  const leadLabel = filters.engagementLead !== 'all' ? filters.engagementLead : 'All Leads'

  const clientLabel = filters.client !== 'all'
    ? clientOrganizations.find(o => o.Id === Number(filters.client))?.DisplayName ?? filters.client
    : 'All Clients'

  const fromDate = `${filters.fromMonth}-01`
  const toDate = (() => {
    const [y, m] = filters.toMonth.split('-').map(Number)
    const d = new Date(y, m, 0) // last day of month (local)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  })()

  const getStatusLabel = (code: string) =>
    engagementStatuses.find(s => s.code === code)?.value ?? code

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">Error loading portfolio analytics: {error}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4 p-4" id="portfolio-dashboard">
      {/* Info bar */}
      <PortfolioInfoBar
        branchLabel={branchLabel}
        leadLabel={leadLabel}
        clientLabel={clientLabel}
        startDate={fromDate}
        endDate={toDate}
      />

      {/* Filter bar */}
      <FilterBar
        filters={filters}
        onChange={handleFilterChange}
        branches={branches}
        clientOrganizations={clientOrganizations}
        leadOptions={leadOptions}
        yearRange={yearRange}
        billingTypes={billingTypes}
        engagementTypes={engagementTypes}
        engagementStatuses={engagementStatuses}
        workerTypeOptions={workerTypeOptions}
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            <p className="mt-4 text-sm text-muted-foreground">Loading portfolio analytics…</p>
          </div>
        </div>
      ) : (
        <>
          {portfolioFinanceError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              Failed to load financial data — KPI totals and some charts may be unavailable.
            </div>
          )}

          {/* KPI Cards — suppressed when finance fetch failed to avoid misleading all-zero display */}
          {!portfolioFinanceError && (
            <PortfolioKPICards
              metrics={portfolioMetrics}
              statusCounts={statusCounts}
            />
          )}

          {/* Charts row 1 — time-series from the MonthlyData report */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ProfitabilityChart
              data={monthlyData}
              contractAmount={chartTotals.contractAmount}
              projectedCost={chartTotals.projectedCost}
            />
            <BurndownChart
              data={monthlyData}
              contractAmount={chartTotals.contractAmount}
              projectedCost={chartTotals.projectedCost}
            />
            <ContractorTypeChart data={workerTypeData} />
          </div>

          {/* Charts row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <GrossMarginByBranchChart data={branchMarginData} />
            <GrossProfitChart
              data={monthlyData}
              contractAmount={chartTotals.contractAmount}
              projectedCost={chartTotals.projectedCost}
            />
            <RevenueRecognizedChart
              data={monthlyData}
              billedToDate={portfolioMetrics.billedToDate}
              revenueToDate={portfolioMetrics.revenueToDate}
            />
          </div>
        </>
      )}

      {/* Utilization Chart — weekly billable utilization by worker type / procurement source */}
      <UtilizationChart
        data={utilizationData}
        isLoading={utilizationLoading}
        procurementSourceOptions={procurementSourceOptions}
        selectedProcurementSource={utilizationProcurementSource}
        onProcurementSourceChange={setUtilizationProcurementSource}
      />

      {/* Engagement financial summary table */}
      <PortfolioEngagementTable
        data={engagementTableData}
        isLoading={engagementTableLoading}
        error={engagementTableError}
        getStatusLabel={getStatusLabel}
        rollUp={rollUp}
        onRollUpChange={setRollUp}
        fetchUnrolledRows={fetchUnrolledRows}
      />

      {/* Team Hours by Engagement Lead */}
      <PortfolioTeamHoursTable
        data={portfolioTeamHoursData}
        isLoading={portfolioTeamHoursLoading}
        error={portfolioTeamHoursError}
      />
    </div>
  )
}
